import { z } from "zod";

import { findCountryByDialCode, getPhoneValidation, stripTrunkPrefix } from "@/lib/phoneValidation";

/**
 * Country-agnostic E.164 regex.
 *
 * - Must start with `+`
 * - Country code first digit 1-9 (no leading zero)
 * - Total of 7-15 digits after the `+`
 *
 * See https://en.wikipedia.org/wiki/E.164
 */
export const E164_REGEX = /^\+[1-9]\d{6,14}$/;

export const E164_MESSAGE =
  "Phone must start with the country code, e.g. +45 12 34 56 78 or +46 70 123 45 67";

/**
 * Strip the separators people naturally type ("+45 4212 6512",
 * "+46 (70) 123-45-67", "+45.42.12.65.12") so the value can be validated and
 * stored as plain E.164 (backend#912). A national trunk 0 written after the
 * dial code of a country that uses one ("+46 070 123 45 67",
 * "+44 (0)7911 123456") is dropped too, since it is never dialled from
 * abroad (#1059).
 */
export function normalizePhone(value: string): string {
  const compact = value.trim().replace(/[\s\-().]/g, "");
  const country = findCountryByDialCode(compact);
  if (!country) {
    return compact;
  }
  return country.dialCode + stripTrunkPrefix(country, compact.slice(country.dialCode.length));
}

/**
 * The plain E.164 form of a free-text phone value when it is one (see
 * `normalizePhone`), otherwise the value as typed. For fields that accept
 * any text but should not store a trunk 0 or separators in an international
 * number, like the consultant's own profile (#1059).
 */
export function tidyPhone(value: string): string {
  const normalized = normalizePhone(value);
  return E164_REGEX.test(normalized) ? normalized : value;
}

/**
 * Returns a Zod schema for an OPTIONAL phone field that enforces E.164 on
 * any value the user has changed, but allows the `initialValue` (legacy
 * stored value) to pass through unchanged.
 *
 * Rationale: existing rows in the database may contain non-E.164 phone
 * numbers (e.g. "12 34 56 78", "+45 12 34 56 78"). When an admin opens an
 * edit form we don't want to greet them with an immediate validation
 * error — but if they edit the field, they must commit to a valid E.164
 * string before the form can be submitted.
 *
 * For create-new flows (no initial value), every non-empty value must be
 * a valid E.164 string once spaces, dashes, dots and parentheses are
 * removed; the parsed (submitted) value is that normalised form.
 */
export function phoneSchema(initialValue?: string | null) {
  const baseline = (initialValue ?? "").trim();
  return (
    z
      .string()
      .trim()
      .optional()
      .refine(
        val => {
          const v = (val ?? "").trim();
          if (v === "") {
            return true; // optional — empty is fine
          }
          if (baseline !== "" && v === baseline) {
            return true; // unchanged legacy value
          }
          return E164_REGEX.test(normalizePhone(v));
        },
        { message: E164_MESSAGE }
      )
      .transform(val => {
        const v = (val ?? "").trim();
        if (v === "" || (baseline !== "" && v === baseline)) {
          return val; // empty or untouched legacy value: leave as-is
        }
        return normalizePhone(v); // what the user typed, minus separators
      })
      // Keep the key optional in the parsed type so the forms' input and output
      // types stay identical (react-hook-form + zodResolver).
      .optional()
  );
}

/** Shown when a phone value carries no dial code from the country picker. */
export const PHONE_COUNTRY_MISSING_MESSAGE = "Please select a country and enter a phone number";

/**
 * The local digits of a `PhoneInput` value (dial code + local digits) with
 * any separators and one trunk prefix removed.
 */
function countryLocalDigits(value: string) {
  const country = findCountryByDialCode(value);
  if (!country) {
    return null;
  }
  const digits = value.slice(country.dialCode.length).replace(/\D/g, "");
  return { country, localDigits: stripTrunkPrefix(country, digits) };
}

/**
 * Validation message for a value produced by `PhoneInput` (dial code + local
 * digits), or null when it is valid. The local part must have the digit
 * count of the picked country, which also makes the result E.164. Shared by
 * the public signup and the admin "Add Joiner" form (backend#885). A trunk 0
 * is not counted (#1059): `normalizeCountryPhone` drops it before saving.
 */
export function countryPhoneError(value: string): string | null {
  const parsed = countryLocalDigits(value);
  if (!parsed) {
    return PHONE_COUNTRY_MISSING_MESSAGE;
  }
  const { country, localDigits } = parsed;
  const { minDigits, maxDigits, errorMessage } = getPhoneValidation(country.code);
  if (localDigits.length < minDigits || localDigits.length > maxDigits) {
    return errorMessage;
  }
  return null;
}

/**
 * The E.164 value to save for a `PhoneInput` value: dial code + local digits,
 * without separators or a trunk 0. `PhoneInput` already emits this form; this
 * also covers a value loaded from an older save, such as +460701234567
 * (#1059). Values without a known dial code are returned unchanged.
 */
export function normalizeCountryPhone(value: string): string {
  const parsed = countryLocalDigits(value);
  return parsed ? parsed.country.dialCode + parsed.localDigits : value;
}

/**
 * Required phone field fed by `PhoneInput`: validated with
 * `countryPhoneError`, submitted as `normalizeCountryPhone`.
 */
export const countryPhoneSchema = z
  .string()
  .superRefine((val, ctx) => {
    const message = countryPhoneError(val);
    if (message) {
      ctx.addIssue({ code: "custom", message });
    }
  })
  .transform(normalizeCountryPhone);
